/**
 * A whole-document edit, expressed as the ops this server already takes.
 *
 * ==================================================================
 * WHY A DIFF AND NOT A STREAM OF STEPS
 * ==================================================================
 * ProseMirror emits steps at character granularity; the op vocabulary is
 * block-granular. SPECIFICATION §6 says exactly why mapping one to the other
 * does not work: "A debounced burst of typing becomes a block `replace`, which
 * destroys every comment anchor in that block", and "rebase for two people
 * typing in one paragraph is operational transformation under another name."
 *
 * So the editor does not translate steps. It waits until the typing stops and
 * asks a narrower question: WHICH BLOCKS ARE DIFFERENT NOW? Blocks carry
 * stable ids, so that question has an exact answer, and the answer is already
 * in the vocabulary -- `replace` what changed, `insert` what appeared,
 * `delete` what went. A paragraph nobody touched produces no op at all, which
 * is what keeps its comment anchors still.
 *
 * The anchor destruction §6 warned about is handled at the other end: a
 * one-for-one replace keeps the block's id and records its edit as a character
 * span, so anchors remap through the text rather than being re-pointed at the
 * whole paragraph (TASK-050).
 *
 * ==================================================================
 * HOW A NEW BLOCK IS RECOGNISED
 * ==================================================================
 * By having no id. Ids are minted by the server and never by a caller
 * (BUG-012), so a block ProseMirror created -- by splitting a paragraph with
 * Enter, or by pasting -- carries none, and that absence is the signal. A
 * block whose id the server has never heard of is treated the same way, since
 * the alternative is asking the server to replace something that is not there.
 */
import type { Op } from './ops.ts'

export interface Block {
  type: string
  attrs?: Record<string, unknown>
  content?: unknown[]
  [key: string]: unknown
}

const idOf = (b: Block): string | null => {
  const id = b.attrs?.id
  return typeof id === 'string' && id ? id : null
}

/** Same block, same content? Compared as the document stores it. */
function same(a: Block, b: Block): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Turn "the document looked like this, now it looks like that" into ops.
 *
 * `before` must be the blocks as the SERVER last gave them, not as the editor
 * last rendered them: the guard hashes ride on those attrs, and a hash from a
 * revision the server never sent would be refused by its own write guard.
 *
 * `touched`, when given, is the set of block ids this browser has actually
 * changed. Only those are considered for a `replace`, and it is not an
 * optimisation -- it is what stops this editor undoing somebody else's work.
 * The editor keeps typing while a remote revision arrives (see DocEditor), so
 * its own view of an untouched paragraph can be older than the server's. A
 * diff that compared everything would see the difference, decide the local
 * version was an edit, and write the other person's paragraph back to what it
 * said before they touched it.
 */
export function diffBlocks(before: Block[], after: Block[], touched?: Set<string>): Op[] {
  const beforeById = new Map<string, Block>()
  for (const b of before) {
    const id = idOf(b)
    if (id) beforeById.set(id, b)
  }
  /*
   * The ids `after` actually claims -- and the ones it claims TWICE.
   *
   * ProseMirror copies a node's attributes when it splits it, so pressing
   * Enter in the middle of a paragraph produces two blocks carrying the SAME
   * id. Ids are unique in a document by construction (the server mints them
   * and nothing else may), so a repeat can only mean a copy: the first
   * occurrence is the block that was already there, and every later one is
   * new. Treating them all as the same block would send two replaces for one
   * id in a single batch, and the paragraph would end up as whichever arrived
   * last.
   *
   * The same rule covers a block duplicated by copy and paste, which is the
   * other way one id comes back twice.
   */
  const afterIds = new Set<string>()
  const seen = new Set<string>()
  const isCopy = new Set<Block>()
  for (const b of after) {
    const id = idOf(b)
    if (!id) continue
    if (seen.has(id)) isCopy.add(b)
    else {
      seen.add(id)
      afterIds.add(id)
    }
  }

  const ops: Op[] = []

  /*
   * Deletions first.
   *
   * A block that has gone takes its position with it, and every `insert` below
   * is anchored to a block that is still there -- so doing these first means
   * no insert is ever pointed at something this same batch is about to remove.
   */
  for (const b of before) {
    const id = idOf(b)
    if (id && !afterIds.has(id)) {
      ops.push({
        op: 'delete',
        target: { kind: 'blocks', ids: [id] },
        // The person removed a paragraph somebody had commented on. They are
        // asked before this is sent; see the editor's detach prompt.
        allowDetach: true,
      })
    }
  }

  /*
   * Then, in document order, what changed and what is new.
   *
   * `anchor` walks with us: it is the last block that EXISTS on the server, so
   * a run of new paragraphs is inserted one after another in the right order
   * rather than all landing in the same place.
   */
  let anchor: string | null = null
  const pendingNew: Block[] = []

  const flushNew = () => {
    if (!pendingNew.length) return
    const content = pendingNew.map((b) => stripIds(b))
    if (anchor) {
      ops.push({
        op: 'insert',
        target: { kind: 'blocks', ids: [anchor] },
        side: 'after',
        source: { from: { kind: 'inline', content }, as: 'blocks' },
      })
    } else {
      // Nothing above them: they go at the top of the tab. `root`, which is
      // what targets.ts calls the tab -- not `doc`, which is nothing.
      ops.push({
        op: 'insert',
        target: { kind: 'root' },
        side: 'start',
        source: { from: { kind: 'inline', content }, as: 'blocks' },
      })
    }
    pendingNew.length = 0
  }

  for (const b of after) {
    const id = idOf(b)
    const known = id && !isCopy.has(b) ? beforeById.get(id) : undefined
    if (!known) {
      pendingNew.push(b)
      continue
    }
    // A run of new blocks ends here, and lands after whatever preceded it.
    flushNew()
    const mine = !touched || (id !== null && touched.has(id))
    if (mine && !same(known, b)) {
      ops.push({
        op: 'replace',
        target: { kind: 'blocks', ids: [id!] },
        with: { from: { kind: 'inline', content: [b] }, as: 'blocks' },
        // The hash the server last gave us for this block. If an agent wrote
        // to it while this was being typed, the server refuses rather than
        // overwriting, and the person is told.
        ...(typeof known.attrs?.h === 'string' ? { ifHash: known.attrs.h as string } : {}),
      })
    }
    anchor = id
  }
  flushNew()

  return ops
}

/**
 * Strip every id from a block being INSERTED, at every depth.
 *
 * A new block has no id of its own, but one pasted from elsewhere in the same
 * document arrives with the ids of what it was copied from -- and those are
 * live, pointing at the blocks still sitting above. The server mints ids for
 * inserted content and would ignore these anyway; sending them is asking a
 * duplicate to be resolved by somebody else's rule. BUG-012 is the reason
 * that rule exists.
 */
function stripIds(block: Block): Block {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(block)) {
    if (key === 'attrs') {
      const attrs: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        // `h` and `rev` go too: they describe a revision of a block that is
        // not this one.
        if (k === 'id' || k === 'h' || k === 'rev') continue
        attrs[k] = v
      }
      if (Object.keys(attrs).length) out.attrs = attrs
      continue
    }
    if (key === 'content' && Array.isArray(value)) {
      out.content = value.map((c) => stripIds(c as Block))
      continue
    }
    out[key] = value
  }
  return out as Block
}
